export interface ProjectionControlPanelProps {
  sessionId: string;
  currentMode: string;
  isConnected: boolean;
  onSetMode: (mode: string) => void;
  large?: boolean;
  compact?: boolean;
}

const PROJECTION_MODES = [
  { type: 'standby', icon: '⏸️', label: 'Attesa', description: 'Schermata predefinita' },
  { type: 'sheet-vote', icon: '🗳️', label: 'Votazione', description: 'Votazione eroi' },
  { type: 'sheet-vote-results', icon: '📊', label: 'Risultati', description: 'Mostra percentuali' },
  { type: 'sheet-vote-winners', icon: '🏆', label: 'Vincitore', description: 'Eroe squadra attiva' },
  { type: 'sheet-vote-all-winners', icon: '👑', label: 'Vincitori', description: 'Tutti gli eroi scelti' },
];

export function ProjectionControlPanel({
  currentMode,
  isConnected,
  onSetMode,
  large = true,
  compact = false,
}: ProjectionControlPanelProps) {
  // Compact mode: minimal UI for quick access
  if (compact) {
    return (
      <div className="flex items-center gap-2 flex-wrap">
        {/* Connection indicator */}
        <div className={`w-3 h-3 rounded-full ${isConnected ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}
             title={isConnected ? 'Connesso' : 'Disconnesso'} />

        {/* Mode buttons */}
        {PROJECTION_MODES.map((mode) => {
          const isActive = currentMode === mode.type;
          return (
            <button
              key={mode.type}
              onClick={() => onSetMode(mode.type)}
              disabled={!isConnected}
              title={mode.description}
              className={`
                px-4 py-2 rounded-lg font-semibold transition-all flex items-center gap-2
                ${isActive
                  ? 'bg-purple-600 text-white ring-2 ring-purple-400 shadow-lg'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-white'}
                ${!isConnected ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              `}
            >
              <span className="text-lg">{mode.icon}</span>
              <span className="text-sm">{mode.label}</span>
            </button>
          );
        })}
      </div>
    );
  }

  // Full mode (original)
  return (
    <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 relative">
      {/* Connection Status Badge */}
      <div className="absolute top-4 right-4">
        {isConnected ? (
          <div className="flex items-center gap-2 bg-green-900/50 px-4 py-2 rounded-full border border-green-500">
            <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
            <span className="text-green-300 text-sm font-semibold">Connesso</span>
          </div>
        ) : (
          <div className="flex items-center gap-2 bg-red-900/50 px-4 py-2 rounded-full border border-red-500">
            <span className="w-2 h-2 bg-red-400 rounded-full"></span>
            <span className="text-red-300 text-sm font-semibold">Disconnesso</span>
          </div>
        )}
      </div>

      <h2 className="text-2xl font-bold text-white mb-2">Controllo Proiezione</h2>
      <p className="text-gray-400 text-sm mb-6">Controlla cosa viene mostrato sulla proiezione</p>

      {/* Current Mode Indicator */}
      <div className="flex items-center gap-3 mb-6">
        <span className="text-gray-400 text-lg">Modalità Corrente:</span>
        <div className="flex items-center gap-2 bg-purple-900 px-6 py-3 rounded-lg border-2 border-purple-500">
          <span className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></span>
          <span className="text-white text-xl font-bold uppercase">{currentMode}</span>
        </div>
      </div>

      {/* Projection Mode Buttons */}
      <div className={`grid ${large ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-4'} gap-4`}>
        {PROJECTION_MODES.map((mode) => {
          const isActive = currentMode === mode.type;
          return (
            <button
              key={mode.type}
              onClick={() => onSetMode(mode.type)}
              disabled={!isConnected}
              className={`
                ${large ? 'p-6' : 'p-4'} rounded-xl border-3 transition-all transform
                ${isActive
                  ? 'bg-purple-600 border-purple-400 scale-105 shadow-lg shadow-purple-500/50'
                  : 'bg-gray-900 border-gray-600 hover:border-gray-500 hover:scale-102'}
                ${!isConnected ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              `}
            >
              <div className={`${large ? 'text-4xl' : 'text-3xl'} mb-2`}>{mode.icon}</div>
              <div className={`${large ? 'text-xl' : 'text-lg'} font-bold text-white`}>{mode.label}</div>
              <div className="text-sm text-gray-400 mt-1">{mode.description}</div>
            </button>
          );
        })}
      </div>

      {!isConnected && (
        <div className="mt-4 p-3 bg-yellow-900/30 border border-yellow-600 rounded-lg">
          <p className="text-yellow-300 text-sm">
            ⚠️ Socket disconnesso. Riconnessione in corso...
          </p>
        </div>
      )}
    </div>
  );
}
